export const DEFAULT_STAFF_PREFERENCES = Object.freeze({
  theme: 'standard',
  textSize: 'standard',
  textWeight: 'regular',
  reduceMotion: false,
  strongFocus: false,
});

const THEMES = new Set(['standard', 'dark', 'high-contrast']);
const TEXT_SIZES = new Set(['standard', 'large', 'x-large']);
const TEXT_WEIGHTS = new Set(['regular', 'strong']);

export function staffPreferenceKey(userId) {
  return `divoices.staff.preferences.v1.${encodeURIComponent(String(userId))}`;
}

export function staffSetupDismissalKey(userId) {
  return `divoices.staff.preferences-invitation.v1.${encodeURIComponent(String(userId))}`;
}

export function parseStaffPreferences(value) {
  try {
    const parsed = JSON.parse(value);
    if (!parsed || !THEMES.has(parsed.theme) || !TEXT_SIZES.has(parsed.textSize)
      || !TEXT_WEIGHTS.has(parsed.textWeight) || typeof parsed.reduceMotion !== 'boolean'
      || typeof parsed.strongFocus !== 'boolean') return { ...DEFAULT_STAFF_PREFERENCES };
    return {
      theme: parsed.theme,
      textSize: parsed.textSize,
      textWeight: parsed.textWeight,
      reduceMotion: parsed.reduceMotion,
      strongFocus: parsed.strongFocus,
    };
  } catch {
    return { ...DEFAULT_STAFF_PREFERENCES };
  }
}

export function readStaffPreferences(userId, storage) {
  if (!userId) return { ...DEFAULT_STAFF_PREFERENCES };
  try {
    const target = storage ?? globalThis.localStorage;
    const value = target?.getItem(staffPreferenceKey(userId));
    return value ? parseStaffPreferences(value) : { ...DEFAULT_STAFF_PREFERENCES };
  } catch {
    return { ...DEFAULT_STAFF_PREFERENCES };
  }
}

export function writeStaffPreferences(userId, preferences, storage) {
  const safe = parseStaffPreferences(JSON.stringify(preferences));
  if (!userId) return { preferences: safe, saved: false };
  try {
    const target = storage ?? globalThis.localStorage;
    if (typeof target?.setItem !== 'function') return { preferences: safe, saved: false };
    const key = staffPreferenceKey(userId); const serialized = JSON.stringify(safe);
    target.setItem(key, serialized);
    return { preferences: safe, saved: target.getItem?.(key) === serialized };
  } catch {
    return { preferences: safe, saved: false };
  }
}

export function applyStaffPreferences(element, preferences) {
  if (!element) return;
  const safe = parseStaffPreferences(JSON.stringify(preferences));
  element.dataset.staffTheme = safe.theme;
  element.dataset.staffTextSize = safe.textSize;
  element.dataset.staffTextWeight = safe.textWeight;
  element.dataset.staffReduceMotion = String(safe.reduceMotion);
  element.dataset.staffStrongFocus = String(safe.strongFocus);
}

export function clearAppliedStaffPreferences(element) {
  if (!element) return;
  delete element.dataset.staffTheme;
  delete element.dataset.staffTextSize;
  delete element.dataset.staffTextWeight;
  delete element.dataset.staffReduceMotion;
  delete element.dataset.staffStrongFocus;
}

export function isStaffSetupInvitationDismissed(userId, storage) {
  if (!userId) return true;
  try {
    const target = storage ?? globalThis.localStorage;
    return target?.getItem(staffSetupDismissalKey(userId)) === 'dismissed';
  } catch {
    return false;
  }
}

export function dismissStaffSetupInvitation(userId, storage) {
  if (!userId) return false;
  try {
    const target = storage ?? globalThis.localStorage;
    if (typeof target?.setItem !== 'function') return false;
    target.setItem(staffSetupDismissalKey(userId), 'dismissed');
    return true;
  } catch {
    return false;
  }
}
